import { defineStore } from "pinia";
import { ref } from "vue";
import customerReviewService from "@/services/customerReview";
import { CustomerReviewTripDetails, DriverReview, VehicleReview, DriverImprovementAttributeEnum, VehicleImprovementAttributeEnum } from "@/models/dto/CustomerReview";

export const useCustomerReviewStore = defineStore(
  "customerReview",
  () => {
    const loading = ref<boolean>(false);
    const updating = ref<boolean>(false);
    const notFound = ref<boolean>(false);

    const tripDetails = ref<CustomerReviewTripDetails>({
      tripName: "",
      reservationManagedId: "",
      tripDate: "",
      scheduledDurationInMinutes: 0,
      drivers: [],
      vehicles: [],
    });

    const overallRating = ref<number>(0);

    const driverReviews = ref<Array<DriverReview>>([]);
    const vehicleReviews = ref<Array<VehicleReview>>([]);

    const useOverallDriverReview = ref(false);
    const useOverallVehicleReview = ref(false);
    const overallDriverRating = ref<number>(0);
    const overallVehicleRating = ref<number>(0);
    const overallDriverComment = ref<string>("");
    const overallVehicleComment = ref<string>("");
    const overallDriverImprovementAttributes = ref<Array<DriverImprovementAttributeEnum>>([]);
    const overallVehicleImprovementAttributes = ref<Array<VehicleImprovementAttributeEnum>>([]);

    const currentStepIdx = ref<number>(0);

    const userShouldRedirectToGoogle = ref<boolean>(false);

    const fetchReviewDetails = async (reservationId: string, customerId: string): Promise<void> => {
      loading.value = true;
      try {
        const response = await customerReviewService.getCustomerReviewDetails(reservationId, customerId);
        const { trip, review } = response.data;

        tripDetails.value = {
          tripName: trip.tripName,
          reservationManagedId: trip.reservationManagedId,
          tripDate: trip.tripDate,
          scheduledDurationInMinutes: trip.scheduledDurationInMinutes,
          drivers: trip.drivers,
          vehicles: trip.vehicles,
        };

        overallRating.value = review.overallRating;

        useOverallDriverReview.value = review.useOverallDriverReview;
        overallDriverRating.value = review.overallDriverRating;
        overallDriverComment.value = review.overallDriverComment;
        overallDriverImprovementAttributes.value = review.overallDriverImprovementAttributes;

        useOverallVehicleReview.value = review.useOverallVehicleReview;
        overallVehicleRating.value = review.overallVehicleRating;
        overallVehicleComment.value = review.overallVehicleComment;
        overallVehicleImprovementAttributes.value = review.overallVehicleImprovementAttributes;

        driverReviews.value = trip.drivers.map(driver => {
          const driverReview = review.driverReviews.find(review => review.driverExternalId === driver.driverExternalId);
          return {
            driverExternalId: driver.driverExternalId,
            driverName: driver.driverName,
            rating: driverReview?.rating || 0,
            comment: driverReview?.comment || "",
            driverImprovementAttributes: driverReview?.driverImprovementAttributes || [],
          };
        });

        vehicleReviews.value = trip.vehicles.map(vehicle => {
          const vehicleReview = review.vehicleReviews.find(review => review.vehicleExternalId === vehicle.vehicleExternalId);
          return {
            vehicleExternalId: vehicle.vehicleExternalId,
            rating: vehicleReview?.rating || 0,
            comment: vehicleReview?.comment || "",
            vehicleImprovementAttributes: vehicleReview?.vehicleImprovementAttributes || [],
            licensePlate: vehicle.licensePlate,
            vehicleTypeLabel: vehicle.vehicleTypeLabel,
          };
        });

        if (!review.overallRating) {
          currentStepIdx.value = 0;
        } else if (!review.overallDriverRating) {
          currentStepIdx.value = 1;
        } else if (!review.overallVehicleRating) {
          currentStepIdx.value = 2;
        } else {
          currentStepIdx.value = 3;
        }
      } catch (error) {
        console.error("Error fetching customer review details:", error);
        notFound.value = true;
      } finally {
        loading.value = false;
      }
    };

    const updateReview = async (reservationId: string, customerId: string): Promise<void> => {
      updating.value = true;
      try {
        const response = await customerReviewService.updateCustomerReview({
          reservationExternalId: reservationId,
          customerExternalId: customerId,
          overallRating: overallRating.value,
          driverReviews: driverReviews.value.filter(review => review.rating > 0),
          vehicleReviews: vehicleReviews.value.filter(review => review.rating > 0),
          overallDriverRating: overallDriverRating.value,
          overallVehicleRating: overallVehicleRating.value,
          overallDriverComment: overallDriverComment.value,
          overallVehicleComment: overallVehicleComment.value,
          overallDriverImprovementAttributes: overallDriverImprovementAttributes.value,
          overallVehicleImprovementAttributes: overallVehicleImprovementAttributes.value,
          useOverallDriverReview: useOverallDriverReview.value,
          useOverallVehicleReview: useOverallVehicleReview.value,
        });

        userShouldRedirectToGoogle.value = response.data.shouldRedirectToGoogle;

        if (currentStepIdx.value >= 2) {
          if (userShouldRedirectToGoogle.value) {
            updateCurrentStepIdx(4);
          } else {
            updateCurrentStepIdx(3);
          }
        } else {
          updateCurrentStepIdx(currentStepIdx.value + 1);
        }
      } catch (error) {
        console.error("Error updating review:", error);
      } finally {
        updating.value = false;
      }
    };

    const updateCurrentStepIdx = (stepIdx: number): void => {
      currentStepIdx.value = stepIdx;
    };

    const setInitialRating = (rating: number): void => {
      overallRating.value = rating;
      if (rating === 5) {
        driverReviews.value.forEach((review) => {
          review.rating = 5;
          review.driverImprovementAttributes = [];
          review.comment = "";
        });
        vehicleReviews.value.forEach((review) => {
          review.rating = 5;
          review.vehicleImprovementAttributes = [];
          review.comment = "";
        });
      }
    };

    return {
      loading,
      updating,
      notFound,
      currentStepIdx,
      overallRating,
      tripDetails,
      driverReviews,
      vehicleReviews,
      overallDriverRating,
      overallVehicleRating,
      overallDriverComment,
      overallVehicleComment,
      overallDriverImprovementAttributes,
      overallVehicleImprovementAttributes,
      useOverallDriverReview,
      useOverallVehicleReview,
      userShouldRedirectToGoogle,
      fetchReviewDetails,
      updateReview,
      updateCurrentStepIdx,
      setInitialRating,
    };
  }
);
